import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaGem, FaChartLine, FaFileInvoiceDollar, FaGlobe, 
  FaLandmark, FaHandHoldingUsd, FaBriefcase, FaShieldAlt,
  FaCalculator, FaPiggyBank, FaSun, FaBuilding, FaCoins, FaBalanceScale, FaUserTie
} from 'react-icons/fa';

const ServicesSection = () => {
  const servicesList = [
    {
      title: "WEALTH MANAGEMENT",
      description: "Holistic, high-touch advisory to grow, protect, and transfer your legacy.",
      icon: <FaGem size={28} className="text-dark mb-3" />,
      features: ["+ Strategy", "+ Advisory", "+ Family Office"],
      path: "/wealth-management"
    },
    {
      title: "PORTFOLIO MANAGEMENT SERVICES",
      description: "Active multi-asset portfolio allocation across equities and alternative funds.",
      icon: <FaChartLine size={28} className="text-dark mb-3" />,
      features: ["+ Equities", "+ AIFs", "+ Customization"],
      path: "/portfolio-management-services"
    },
    {
      title: "TAX PLANNING",
      description: "Year-round strategic planning to minimize liabilities and maximize net returns.",
      icon: <FaFileInvoiceDollar size={28} className="text-dark mb-3" />,
      features: ["+ Optimization", "+ Compliance", "+ Capital Gains"],
      path: "/tax-planning"
    },
    {
      title: "OFFSHORE PRODUCTS",
      description: "Cross-border financial solutions and international asset diversification.",
      icon: <FaGlobe size={28} className="text-dark mb-3" />,
      features: ["+ Global Funds", "+ NRI Services", "+ Currency Spread"],
      path: "/offshore-products"
    },
    {
      title: "ESTATE PLANNING",
      description: "Structuring wills and private family trusts for smooth wealth transfer.",
      icon: <FaLandmark size={28} className="text-dark mb-3" />,
      features: ["+ Wills Setup", "+ Family Trusts", "+ Succession"],
      path: "/estate-planning"
    },
    {
      title: "ALTERNATE INVESTMENTS",
      description: "Access to high-growth non-traditional asset classes and structured products.",
      icon: <FaCoins size={28} className="text-dark mb-3" />,
      features: ["+ Commodities", "+ Real Estate", "+ Structured Notes"],
      path: "/alternate-investments"
    },
    {
      title: "PRIVATE EQUITY",
      description: "Direct participation in high-potential private companies and ventures.",
      icon: <FaBriefcase size={28} className="text-dark mb-3" />,
      features: ["+ Growth Capital", "+ Pre-IPO", "+ Venture Deals"],
      path: "/private-equity"
    },
    {
      title: "PRIVATE CREDIT",
      description: "Yield-generating private debt instruments and tailored corporate lending.",
      icon: <FaBalanceScale size={28} className="text-dark mb-3" />,
      features: ["+ Direct Lending", "+ Fixed Yields", "+ Security Backed"],
      path: "/private-credit"
    },
    {
      title: "FINANCIAL PLANNING",
      description: "Comprehensive blueprints combining cash flows, liabilities, and long-term goals.",
      icon: <FaCalculator size={28} className="text-dark mb-3" />,
      features: ["+ Cash Flows", "+ Risk Profiling", "+ Roadmaps"],
      path: "/financial-planning"
    },
    {
      title: "GOAL BASED INVESTMENTS",
      description: "Dedicated asset allocation mapped directly to your major life milestones.",
      icon: <FaPiggyBank size={28} className="text-dark mb-3" />,
      features: ["+ Education Funds", "+ Home Buying", "+ Milestone Tracking"],
      path: "/goal-based-investments"
    },
    {
      title: "RETIREMENT PLANNING",
      description: "Structured accumulation phases ensuring financial security post-career.",
      icon: <FaShieldAlt size={28} className="text-dark mb-3" />,
      features: ["+ Pension Setup", "+ Corpus Building", "+ Risk Mitigation"],
      path: "/retirement-planning"
    },
    {
      title: "POST RETIREMENT SOLUTIONS",
      description: "Safe income generation streams and capital preservation during retirement.",
      icon: <FaHandHoldingUsd size={28} className="text-dark mb-3" />,
      features: ["+ Regular Payouts", "+ Healthcare Funds", "+ Safety First"],
      path: "/post-retirement-solutions"
    },
    {
      title: "FIRE PLANNING",
      description: "Accelerated saving and compounding models for Financial Independence, Retire Early.",
      icon: <FaSun size={28} className="text-dark mb-3" />,
      features: ["+ Lean/Fat FIRE", "+ High Savings", "+ Early Exit"],
      path: "/fire-planning"
    },
    {
      title: "CORPORATE FINANCIAL WELLNESS",
      description: "Employee financial literacy programs, benefits advisory, and executive planning.",
      icon: <FaBuilding size={28} className="text-dark mb-3" />,
      features: ["+ Workshops", "+ Employee Perks", "+ Executive Care"],
      path: "/corporate-financial-wellness"
    },
    {
      title: "ASSET MANAGEMENT",
      description: "Rigorous institutional oversight, risk monitoring, and multi-class custody.",
      icon: <FaUserTie size={28} className="text-dark mb-3" />,
      features: ["+ Risk Oversight", "+ Custody Review", "+ Rebalancing"],
      path: "/asset-management"
    }
  ];

  return (
    <section
      className="py-5 position-relative overflow-hidden services-custom-bg"
      style={{
        backgroundImage: "url('/images/lotos-wealth-bg2.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="container py-4 position-relative" style={{ zIndex: 2 }}>
        
        {/* Top Header Row with Scroll Animation */}
        <div className="row align-items-center mb-5">
          <div className="col-lg-6 mb-4 mb-lg-0 animate-slide-up">
            <h2 className="display-4 fw-bold text-dark text-uppercase" style={{ letterSpacing: '-1px', lineHeight: '1.1' }}>
              Smart Investments.  <br />Stronger Returns.<br />Secure Future..
            </h2>
          </div>
          <div className="col-lg-6 animate-slide-up delay-2">
            <p className="text-dark fw-medium lead fs-6" style={{ maxWidth: '400px' }}>
              Comprehensive wealth advisory, smart investment strategies, and proactive portfolio management designed to protect your assets and accelerate long-term financial growth. Explore our 15+ premium financial services tailored to your goals.
            </p>
          </div>
        </div>

        {/* Services Cards Grid with staggered scroll animations */}
        <div className="row g-4">
          {servicesList.map((service, index) => (
            <div 
              key={index} 
              className={`col-lg-4 col-md-6 animate-slide-up delay-${(index % 3) + 1}`}
            >
              <div className="service-card p-4 rounded-4 bg-white-one border position-relative h-100 shadow-sm transition-all hover-lift d-flex flex-column justify-content-between">
                
                <div>
                  {/* Icon */}
                  {service.icon}

                  {/* Title */}
                  <h5 className="fw-bold text-dark fs-6 mb-2" style={{ letterSpacing: '0.5px' }}>
                    {service.title}
                  </h5>

                  {/* Small Description */}
                  <p className="text-muted small mb-3">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul className="list-unstyled text-secondary small mb-4">
                    {service.features.map((feat, idx) => (
                      <li key={idx} className="mb-1">{feat}</li>
                    ))}
                  </ul>
                </div>

                {/* Read More Link fixed at the bottom right corner */}
                <div className="text-end mt-auto pt-2">
                  <Link 
                    to={service.path} 
                    className="text-decoration-none fw-bold small text-teal d-inline-flex align-items-center"
                    style={{ color: '#026555', transition: 'all 0.2s ease-in-out' }}
                  >
                    Read More <span className="ms-1">→</span>
                  </Link>
                </div>

                {/* Card Notch / Edge Indicator */}
                <div className="card-notch position-absolute top-50 end-0 translate-middle-y bg-dark" style={{ width: '8px', height: '35px', borderRadius: '4px 0 0 4px' }}></div>

              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default ServicesSection;